import React, { useRef, useEffect, useCallback } from "react"
import {
  Stepper,
  Step,
  styled,
  StepConnector,
  useTheme,
  StepLabel,
  Stack,
  StackProps,
  StepIcon,
  Typography,
  IconButton,
} from "@mui/material"
import { Trans } from "react-i18next"
import { deepmerge } from "@mui/utils"

import { useTranslation } from "providers/i18n"

import { StepByStepIcon } from "./StepIcon"
import { OriginMarkerIcon } from "components/icons"
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded"

export interface VerticalStepProgressBarProps extends Pick<StackProps, "sx"> {
  currentStep: number
  steps: any[]
  handleClickStep: (stepIndex: number) => void
  enableAnimateScroll?: boolean
  /**
   * Set start stepper point direction: 'ttb' (Top to Bottom), 'btt' (Bottom to Top),
   */
  direction?: "ttb" | "btt"
  originStepIcon?: JSX.Element
  destinationStepIcon?: JSX.Element
  originStepImage?: JSX.Element
  destinationStepImage?: JSX.Element
  originStepLabel?: string
  destinationStepLabel?: string
}

const StepByStepConnector = styled(StepConnector)(({ theme }) => ({}))

export const VerticalStepProgressBar = ({
  currentStep = 1,
  steps = [],
  sx = {},
  handleClickStep = () => {},
  enableAnimateScroll = false,
  direction = "ttb",
  originStepIcon,
  originStepImage,
  originStepLabel = "Your Location",
  destinationStepIcon,
  destinationStepImage,
  destinationStepLabel,
}: VerticalStepProgressBarProps) => {
  const { t } = useTranslation()

  const lastStep = steps.length - 1
  const destinationStep = steps.length

  const theme = useTheme()
  const {
    stepper,
    originIcon,
    connector,
    wrapper,
    stepperContainer,
    arrowButton,
  } = theme.components[`VerticalStepProgressBar`]?.styleOverrides || {}

  const { highlightedText } =
    theme.components[`DirectionStep`]?.styleOverrides || {}

  const highlightedStepText = (
    //@ts-ignore
    <Typography variant="subTitle" component="span" sx={highlightedText} />
  )

  const scrollRef = useRef([])
  const previousStepRef = useRef(0)

  const scrollToSection = useCallback((index) => {
    if (scrollRef.current.length) {
      /**
       * ScrollIntoView() causing the whole page to move
       * https://stackoverflow.com/questions/11039885/scrollintoview-causing-the-whole-page-to-move
       */
      scrollRef.current[index]?.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "center",
      })
    }
  }, [])

  useEffect(() => {
    if (!enableAnimateScroll) return

    //scroll to origin marker if currentStep is 1
    if (currentStep === 1) {
      scrollToSection(0)
    }
    //scroll to destination marker if currentStep is the arriving destination step
    else if (currentStep === destinationStep) {
      scrollToSection(destinationStep)
    }
    //focus to lesser step if user is going to previous step to avoid currentStep element clipping to container
    else if (currentStep < previousStepRef.current) {
      scrollToSection(currentStep - 1)
    }
    //focus to further step if user is going to next step to avoid currentStep element clipping to container
    else if (currentStep > previousStepRef.current) {
      scrollToSection(currentStep + 1)
    }
    previousStepRef.current = currentStep
  }, [currentStep, destinationStep, scrollToSection, enableAnimateScroll])

  return (
    <Stack sx={deepmerge(wrapper, sx)}>
      <Stepper
        activeStep={currentStep}
        connector={<StepByStepConnector sx={connector} />}
        orientation="vertical"
        sx={deepmerge(stepperContainer, {
          flexDirection: direction === "ttb" ? "column" : "column-reverse",
        })}
      >
        <Step
          sx={stepper}
          className="originStep"
          ref={(ref) => (scrollRef.current[0] = ref)}
        >
          <StepLabel
            StepIconComponent={(labelProps) =>
              originStepIcon || (
                <StepIcon icon={<OriginMarkerIcon sx={originIcon} />} />
              )
            }
          >
            {originStepImage}
            {t(originStepLabel)}
          </StepLabel>
        </Step>
        {steps.map((step, index) => {
          const { description } = step
          return (
            <Step
              key={index}
              className={lastStep === index ? "destinationStep" : ""}
              //ref for step 1 onwards
              ref={(ref) => (scrollRef.current[index + 1] = ref)}
              sx={stepper}
              onClick={() => handleClickStep(index)}
            >
              <StepLabel
                StepIconComponent={(labelProps) => (
                  <StepByStepIcon
                    {...labelProps}
                    step={step}
                    lastStep={lastStep}
                    lastStepIcon={destinationStepIcon}
                    index={index}
                  />
                )}
              >
                {lastStep === index && destinationStepImage}
                {destinationStepLabel ? (
                  t(destinationStepLabel)
                ) : (
                  <Trans
                    components={{
                      category: highlightedStepText,
                      destination: highlightedStepText,
                      landmark: highlightedStepText,
                      intermediary: highlightedStepText,
                      toward: highlightedStepText,
                      turn: highlightedStepText,
                    }}
                  >
                    {t(description.text, {
                      category: t(description.options?.category),
                      destination: t(description.options?.destination),
                      landmark: t(description.options?.landmark),
                      intermediary: t(description.options?.intermediary),
                      toward: t(description.options?.toward),
                      turn: t(description.options?.turn),
                    })}
                  </Trans>
                )}
              </StepLabel>

              {lastStep !== index && (
                <IconButton
                  aria-label="go to step"
                  color="inherit"
                  size="small"
                  sx={arrowButton}
                >
                  <ArrowForwardIosRoundedIcon
                    fontSize="medium"
                    color="primary"
                  />
                </IconButton>
              )}
            </Step>
          )
        })}
      </Stepper>
    </Stack>
  )
}
